import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Icon } from '@/common/components/Icon'
import Loader from '@/common/components/Loader'
import { Header, PageTitle } from '@/components'
import {
  Button,
  Card,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import { getVendors, IVendor } from '@/core/services/vendors'
import { MessageBox } from '@/home/components/messages/MessageBox'
import { LocationsTab } from '@/home/components/vendors/tabs/LocationsTab'
import { ProductsTab } from '@/home/components/vendors/tabs/ProductsTab'
import { TagsTab } from '@/home/components/vendors/tabs/TagsTab'
import { UsersTab } from '@/home/components/vendors/tabs/UsersTab'

export const VendorDetailsPage: React.FC = () => {
  const navigate = useNavigate()
  const { editionId, festivalName, vendorId } = useParams()

  const [vendor, setVendor] = useState<IVendor | null>(null)
  const [loading, setLoading] = useState(false)
  const [tab, setTab] = useState('locations')

  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const handleError = (msg: string) => {
    setIsError(true)
    setShowMessageBox(true)
    setMessage(msg)
  }

  const getVendorDetails = useCallback(async () => {
    if (!vendorId) return
    setLoading(true)
    try {
      const { data: vendors } = await getVendors({
        festivalEditionId: editionId as string,
      })
      const vendor = vendors.find((v) => v.id === vendorId)
      if (vendor) {
        setVendor(vendor)
      }
    } catch (e) {
      if (e instanceof Error) handleError(e.message)
    } finally {
      setLoading(false)
    }
  }, [vendorId])

  useEffect(() => {
    getVendorDetails()
  }, [getVendorDetails])

  return (
    <>
      <Header pageTitle={vendor?.name || 'Vendor Details'} />
      {loading && <Loader />}

      <Tabs value={tab} onValueChange={setTab} className="w-full mt-4">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="secondary"
            className="border border-monochrome-120"
            onClick={() => {
              navigate(`/vendors/${festivalName}/${editionId}`)
            }}
          >
            <Icon
              icon="arrow-left"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            />
            Back
          </Button>
          <TabsList className="grid w-full">
            <div className="bg-white/10 p-2.5 rounded-[20px]">
              <TabsTrigger value="locations">Locations</TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="tags">Tags</TabsTrigger>
              <TabsTrigger value="products">Products</TabsTrigger>
            </div>
          </TabsList>
          <Button variant="primary" onClick={() => setCreateModalOpen(true)}>
            Add New
            <Icon
              icon="plus-circle"
              width="18"
              height="18"
              viewBox="0 0 18 18"
            />
          </Button>
        </div>

        <Card className="!bg-monochrome4-60 p-5 backdrop-blur-lg max-h-[calc(100svh-280px)] overflow-y-auto">
          <PageTitle title={vendor?.name || ''} icon="map" />

          <TabsContent value="locations" className="mt-0">
            <LocationsTab
              vendorId={vendorId!}
              onError={handleError}
              createModalOpen={createModalOpen}
              onCreateModalChange={setCreateModalOpen}
            />
          </TabsContent>

          <TabsContent value="users" className="mt-0">
            <UsersTab
              vendorId={vendorId!}
              onError={handleError}
              createModalOpen={createModalOpen}
              onCreateModalChange={setCreateModalOpen}
            />
          </TabsContent>

          <TabsContent value="tags" className="mt-0">
            <TagsTab
              vendorId={vendorId!}
              onError={handleError}
              createModalOpen={createModalOpen}
              onCreateModalChange={setCreateModalOpen}
            />
          </TabsContent>

          <TabsContent value="products" className="mt-0">
            <ProductsTab
              vendorId={vendorId!}
              onError={handleError}
              createModalOpen={createModalOpen}
              onCreateModalChange={setCreateModalOpen}
            />
          </TabsContent>
        </Card>
      </Tabs>

      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
